import { MoreHorizontal, Pencil, Rocket, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Parts shared by the tables of the Agentendienste (prompt tools, knowledge bases, agent toolsets,
 * workflows): a yes/no state as a badge and the row menu at the end of the row.
 */

/** Yes/no state as a badge — readable without relying on the colour of a tick. */
export function YesNoBadge({ active }: { active: boolean }) {
    const { t } = useTranslation();
    return (
        <Badge variant={active ? "success" : "outline"} className={active ? undefined : "text-muted-foreground"}>
            {active ? t("global.yes") : t("global.no")}
        </Badge>
    );
}

/**
 * The "…" menu at the end of a row: edit, test, delete. A click on the row opens the editor, so the
 * menu must not pass its own clicks on to the row.
 */
export function OfferRowMenu({
    onEdit,
    onTest,
    onDelete,
}: {
    onEdit: () => void;
    onTest?: () => void;
    onDelete: () => void;
}) {
    const { t } = useTranslation();
    return (
        <div className="flex justify-end" onClick={(event) => event.stopPropagation()}>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label={t("ui.actions", "Aktionen")}>
                        <MoreHorizontal className="h-4 w-4" aria-hidden />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={onEdit}>
                        <Pencil className="mr-2 h-4 w-4" aria-hidden />
                        {t("ui.edit")}
                    </DropdownMenuItem>
                    {onTest && (
                        <DropdownMenuItem onSelect={onTest}>
                            <Rocket className="mr-2 h-4 w-4" aria-hidden />
                            {t("ui.test")}
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={onDelete}>
                        <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                        {t("ui.delete")}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}

/** The column definition fragment for the row menu; `DataTable` shows a single button as its skeleton. */
export const rowMenuColumnMeta = { skeleton: "icon", className: "w-14 text-right" } as const;
